import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Container, Grid, 
    Chip, TextField, Button, Avatar, Stack,
    CircularProgress, Fade, Zoom, IconButton, Tooltip,
    InputAdornment
} from '@mui/material';
import {
    Assignment as HomeworkIcon,
    Person as TeacherIcon,
    CalendarMonth as DateIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Download as DownloadIcon,
    School as SubjectIcon,
    PriorityHigh as OverdueIcon,
    CheckCircle as DoneIcon,
    Schedule as PendingIcon,
    Visibility as ViewIcon,
    Timer as TimerIcon
} from '@mui/icons-material';
import axios from 'axios';
import { formatDatePart, getMonthNameFromDate, getCurrentBSDate } from '../../utils/customDate';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 100px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const GlassCard = styled(Paper)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &:hover {
        box-shadow: 0 20px 50px -12px rgba(0, 0, 0, 0.1) !important;
    }
`;

const StatCard = styled(Paper)`
    padding: 24px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const HomeworkItem = styled(Paper)`
    padding: 24px;
    border-radius: 20px;
    background: white;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
    
    &:hover {
        border-color: #6366f1;
        background: #f8fafc;
    }
`;

const StudentHomework = () => {
    const { currentUser } = useSelector((state) => state.user);
    
    const [homework, setHomework] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    const studentClassId = currentUser?.sclassName?._id;

    useEffect(() => {
        if (studentClassId) {
            fetchHomework();
        }
    }, [studentClassId]);

    const fetchHomework = async () => {
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Homework/Student/${studentClassId}`);
            if (result.data && result.data.length > 0) {
                setHomework(result.data);
            } else {
                setHomework([]);
                setMessage({ type: 'info', text: 'No homework found' });
            }
        } catch (error) {
            console.error('Error fetching homework:', error);
            setMessage({ type: 'error', text: 'Error loading homework' });
        }
        setLoading(false);
    };

    const filteredHomework = useMemo(() => {
        return homework.filter(hw => 
            hw.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            hw.subject?.subName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            hw.teacher?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [homework, searchTerm]);

    const getStatusInfo = (dueDate) => {
        const today = getCurrentBSDate();
        const cleanDue = formatDatePart(dueDate);
        
        if (cleanDue < today) return { label: 'Overdue', color: '#ef4444', bg: '#fef2f2', icon: <OverdueIcon fontSize="small" /> };
        if (cleanDue === today) return { label: 'Due Today', color: '#f59e0b', bg: '#fff7ed', icon: <PendingIcon fontSize="small" /> };
        return { label: 'Active', color: '#10b981', bg: '#ecfdf5', icon: <DoneIcon fontSize="small" /> };
    };

    const getDaysRemaining = (dueDate) => {
        const todayStr = getCurrentBSDate();
        const dueStr = formatDatePart(dueDate);
        
        if (dueStr < todayStr) return 'X';
        
        const today = new Date(todayStr);
        const due = new Date(dueStr);
        const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const handleDownload = (hw) => {
        if (hw.filePath) {
            const filename = hw.filePath.split(/[/\\]/).pop();
            const fullUrl = `${process.env.REACT_APP_BASE_URL}/uploads/homework/${encodeURIComponent(filename)}`;
            window.open(fullUrl, '_blank');
        } else {
            alert('No attachment provided.');
        }
    };

    const stats = useMemo(() => {
        const today = getCurrentBSDate();
        return {
            total: homework.length,
            overdue: homework.filter(hw => formatDatePart(hw.dueDate) < today).length,
            dueToday: homework.filter(hw => formatDatePart(hw.dueDate) === today).length
        };
    }, [homework]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                    <HomeworkIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Assignments
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        Manage and track your school homework and projects.
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ textAlign: 'right', bgcolor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                                    <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Total Assigned</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 900 }}>{stats.total}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="xl" sx={{ mt: 5 }}>
                <Grid container spacing={3}>
                    {/* Navigation Sidebar */}
                    <Grid item xs={12} md={3}>
                        <Stack spacing={3}>
                            <GlassCard sx={{ p: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#64748b', textTransform: 'uppercase' }}>Quick Search</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Search by subject or title..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#94a3b8' }} />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: '12px', bgcolor: '#f8fafc' }
                                    }}
                                />
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchHomework}
                                    sx={{ mt: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                >
                                    Sync Homework
                                </Button>
                            </GlassCard>

                            <StatCard elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444' }}><OverdueIcon /></Avatar>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>OVERDUE</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.overdue} Items</Typography>
                                    </Box>
                                </Box>
                            </StatCard>

                            <StatCard elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: '#fff7ed', color: '#f59e0b' }}><TimerIcon /></Avatar>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>DUE TODAY</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.dueToday} Items</Typography>
                                    </Box>
                                </Box>
                            </StatCard>
                        </Stack>
                    </Grid>

                    {/* Homework Feed */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6' }}><HomeworkIcon /></Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>Assignment Feed</Typography>
                            </Box>
                            <Chip 
                                label={`${filteredHomework.length} Items Found`} 
                                size="small" 
                                sx={{ fontWeight: 700, bgcolor: '#e2e8f0' }} 
                            />
                        </Box>

                        {filteredHomework.length === 0 ? (
                            <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '24px', bgcolor: 'white', border: '1px dashed #cbd5e1' }}>
                                <Avatar sx={{ width: 80, height: 80, bgcolor: '#f1f5f9', color: '#94a3b8', mx: 'auto', mb: 2 }}>
                                    <HomeworkIcon sx={{ fontSize: 40 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>No assignments matching your search</Typography>
                                <Typography variant="body2" color="textSecondary">Try a different search term or sync your homework.</Typography>
                            </Paper>
                        ) : (
                            <Stack spacing={3}>
                                {filteredHomework.map((hw, index) => {
                                    const status = getStatusInfo(hw.dueDate);
                                    const daysLeft = getDaysRemaining(hw.dueDate);
                                    
                                    return (
                                        <Zoom in timeout={400 + index * 100} key={hw._id}>
                                            <HomeworkItem elevation={0}>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} sm={8}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                            <Stack direction="row" spacing={1}>
                                                                <Chip 
                                                                    label={hw.subject?.subName || 'General'} 
                                                                    size="small" 
                                                                    sx={{ bgcolor: '#f1f5f9', fontWeight: 800, borderRadius: '8px' }} 
                                                                />
                                                                <Chip 
                                                                    icon={status.icon}
                                                                    label={status.label} 
                                                                    size="small" 
                                                                    sx={{ bgcolor: status.bg, color: status.color, fontWeight: 800, borderRadius: '8px' }} 
                                                                />
                                                            </Stack>
                                                        </Box>
                                                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>
                                                            {hw.title}
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ color: '#475569', mb: 3, lineHeight: 1.6 }}>
                                                            {hw.description}
                                                        </Typography>
                                                        
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#f8fafc' }}>
                                                                        <TeacherIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                                                    </Avatar>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
                                                                        Teacher: {hw.teacher?.name || 'Assigned'}
                                                                    </Typography>
                                                                </Stack>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#f8fafc' }}>
                                                                        <DateIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                                                    </Avatar>
                                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
                                                                        Due: {formatDatePart(hw.dueDate)}
                                                                    </Typography>
                                                                </Stack>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    
                                                    <Grid item xs={12} sm={4}>
                                                        <Box sx={{ 
                                                            height: '100%', 
                                                            display: 'flex', 
                                                            flexDirection: 'column', 
                                                            justifyContent: 'center', 
                                                            alignItems: 'center',
                                                            bgcolor: '#f8fafc',
                                                            borderRadius: '16px',
                                                            p: 2,
                                                            border: '1px solid #e2e8f0'
                                                        }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', mb: 1 }}>Days Left</Typography>
                                                            <Typography variant="h2" sx={{ fontWeight: 900, color: status.color, lineHeight: 1 }}>
                                                                {daysLeft}
                                                            </Typography>
                                                            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                                                                {hw.filePath && (
                                                                    <Tooltip title="Download Materials">
                                                                        <IconButton onClick={() => handleDownload(hw)} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                                                                            <DownloadIcon sx={{ color: '#6366f1' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                <Button 
                                                                    variant="contained" 
                                                                    size="small"
                                                                    startIcon={<ViewIcon />}
                                                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: '#1e293b' }}
                                                                >
                                                                    Details
                                                                </Button>
                                                            </Stack>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </HomeworkItem>
                                        </Zoom>
                                    );
                                })}
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default StudentHomework;
